import { createFileRoute } from '@tanstack/react-router'
import React, { useEffect, useMemo, useState } from 'react';
import Layout from '@/components/Layout';
import { apiFetch } from '@/utils/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PlusIcon, SunIcon } from '@heroicons/react/24/outline';
import { useI18n } from '@/hooks/useI18n';
import { PendingTimeOffRequestsButton } from '@/components/PendingTimeOffRequestsButton';


interface PolicyType { id: string; name: string }
interface PolicyItem {
    id: string;
    name: string;
    policy_type_id: string;
    policy_type_name?: string | null;
    units: 'hours' | 'days';
    is_unlimited: boolean;
}

interface TimeOffRow {
    employee_id: string;
    first_name: string;
    last_name: string;
    approved_hours: number;
    remaining_hours: number | null;
    is_unlimited: boolean;
    has_day_policies: boolean;
}

interface TimeOffSummaryResponse {
    items: TimeOffRow[];
    year: number;
    policy_type_id?: string | null;
}

interface EmployeeTimeOffPolicyBalance {
    policy_type_id: string;
    policy_type_name: string;
    balance_hours: number | null;
    balance_days: number | null;
    units: string;
    is_unlimited: boolean;
}

interface EmployeeTimeOffRequestItem {
    id: string;
    policy_type_name: string;
    start_timestamp: number;
    end_timestamp: number;
    is_all_day: boolean;
    status: string;
    employee_note: string | null;
    manager_note: string | null;
    created_at: string;
}

interface EmployeeTimeOffDetailResponse {
    employee_id: string;
    first_name: string;
    last_name: string;
    year: number;
    policy_balances: EmployeeTimeOffPolicyBalance[];
    requests: EmployeeTimeOffRequestItem[];
    total_approved_hours: number;
}

function usePolicyTypes() {
    const [types, setTypes] = useState<PolicyType[]>([]);
    useEffect(() => {
        apiFetch('/api/timesheet/time-off-policies/types')
            .then((r) => r.json())
            .then((data) => setTypes(data || []))
            .catch(() => setTypes([]));
    }, []);
    return types;
}

interface PolicyResponse {
    id: string;
    name: string;
    policy_type_id: string;
    policy_type_name?: string | null;
    units: 'hours' | 'days';
    is_unlimited: boolean;
    balance_limit?: number | null;
    accrual_method: 'fixed_amount' | 'earned_per_hour';
    accrual_cycle?: 'all_at_once' | 'per_pay_period' | null;
    earned_units?: number | null;
    per_hours_worked?: number | null;
    requires_admin_approval: boolean;
    carryover_limit_enabled: boolean;
    carryover_limit_value?: number | null;
    sun_hours?: number | null;
    mon_hours?: number | null;
    tue_hours?: number | null;
    wed_hours?: number | null;
    thu_hours?: number | null;
    fri_hours?: number | null;
    sat_hours?: number | null;
    employee_ids: string[];
    is_default: boolean;
}

interface EmployeeItem {
    id: string;
    first_name: string;
    last_name: string;
    is_active: boolean;
}

function useEmployees() {
    const [employees, setEmployees] = useState<EmployeeItem[]>([]);
    useEffect(() => {
        apiFetch('/api/people/employees/?limit=100&is_active=true')
            .then((r) => r.json())
            .then((data) => setEmployees(data?.employees || []))
            .catch(() => setEmployees([]));
    }, []);
    return employees;
}

const emptyForm = {
    name: '',
    policy_type_id: '',
    units: 'hours' as 'hours' | 'days',
    is_unlimited: false,
    is_default: false,
    balance_limit: '' as string | number | '',
    accrual_method: 'fixed_amount' as 'fixed_amount' | 'earned_per_hour',
    accrual_cycle: 'all_at_once' as 'all_at_once' | 'per_pay_period',
    earned_units: '' as string | number | '',
    per_hours_worked: '' as string | number | '',
    requires_admin_approval: false,
    carryover_limit_enabled: false,
    carryover_limit_value: '' as string | number | '',
    sun_hours: '' as string | number | '',
    mon_hours: '' as string | number | '',
    tue_hours: '' as string | number | '',
    wed_hours: '' as string | number | '',
    thu_hours: '' as string | number | '',
    fri_hours: '' as string | number | '',
    sat_hours: '' as string | number | '',
    employee_ids: [] as string[],
};


function usePolicies() {
    const [policies, setPolicies] = useState<PolicyItem[]>([]);
    const [loading, setLoading] = useState(false);
    const loadPolicies = () => {
        setLoading(true);
        apiFetch('/api/timesheet/time-off-policies?per_page=200')
            .then((r) => r.json())
            .then((data) => setPolicies((data?.policies || []).map((p: any) => ({
                id: p.id,
                name: p.name,
                policy_type_id: p.policy_type_id,
                policy_type_name: p.policy_type_name,
                units: p.units,
                is_unlimited: !!p.is_unlimited,
            }))))
            .catch(() => setPolicies([]))
            .finally(() => setLoading(false));
    };
    useEffect(() => { loadPolicies(); }, []);
    return { policies, loading, reload: loadPolicies };
}

export default function TimeOffOverviewPage() {
    const { t } = useI18n();
    const currentYear = new Date().getFullYear();
    const [year, setYear] = useState<number>(currentYear);
    const [policyType, setPolicyType] = useState<string>('');
    const [rows, setRows] = useState<TimeOffRow[]>([]);
    const [loading, setLoading] = useState(false);

    const policyTypes = usePolicyTypes();
    const { policies, reload: reloadPolicies } = usePolicies();
    const employees = useEmployees();

    // Employee detail popup state
    const [selectedEmployee, setSelectedEmployee] = useState<EmployeeTimeOffDetailResponse | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // Edit request state
    const [editingRequest, setEditingRequest] = useState<EmployeeTimeOffRequestItem | null>(null);
    const [editSubmitting, setEditSubmitting] = useState(false);
    const [editError, setEditError] = useState<string | null>(null);

    const years = useMemo(() => {
        const ys: number[] = [];
        for (let i = 0; i < 6; i++) ys.push(currentYear - i);
        return ys;
    }, [currentYear]);

    const load = React.useCallback(() => {
        setLoading(true);
        const q = new URLSearchParams({ year: String(year) });
        if (policyType) q.set('policy_type_id', policyType);
        apiFetch(`/api/timesheets/timeoff/summary?${q.toString()}`)
            .then((r) => r.json())
            .then((data: TimeOffSummaryResponse) => setRows(data?.items || []))
            .finally(() => setLoading(false));
    }, [year, policyType]);

    const loadEmployeeDetail = async (employeeId: string) => {
        setDetailLoading(true);
        try {
            const resp = await apiFetch(`/api/timesheets/timeoff/employee/${employeeId}?year=${year}`);
            if (resp.ok) {
                const data: EmployeeTimeOffDetailResponse = await resp.json();
                setSelectedEmployee(data);
            }
        } catch (err) {
            console.error('Failed to load employee detail:', err);
        } finally {
            setDetailLoading(false);
        }
    };

    const openEditRequest = (request: EmployeeTimeOffRequestItem) => {
        setEditingRequest(request);
        setEditError(null);
    };

    const closeEditRequest = () => {
        setEditingRequest(null);
        setEditError(null);
    };

    const handleUpdateRequest = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingRequest) return;

        setEditError(null);
        setEditSubmitting(true);

        try {
            const formData = new FormData(e.target as HTMLFormElement);
            const date = formData.get('date') as string;
            const isAllDay = formData.get('is_all_day') === 'on';
            const startTime = formData.get('start_time') as string;
            const endTime = formData.get('end_time') as string;
            const employeeNote = formData.get('employee_note') as string;
            const timezone = formData.get('timezone') as string || browserTZ;

            if (!date) throw new Error('Date is required');

            let start_ts = 0, end_ts = 0;
            if (isAllDay) {
                start_ts = toEpochSeconds(date, '00:00');
                end_ts = toEpochSeconds(date, '23:59');
            } else {
                if (!startTime || !endTime) throw new Error('Start and end time are required');
                start_ts = toEpochSeconds(date, startTime);
                end_ts = toEpochSeconds(date, endTime);
                if (end_ts < start_ts) throw new Error('End time must be after start time');
            }

            const payload = {
                start_timestamp: start_ts,
                end_timestamp: end_ts,
                timezone,
                is_all_day: isAllDay,
                employee_note: employeeNote || null,
            };

            const resp = await apiFetch(`/api/timesheet/time-off-requests/${editingRequest.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!resp.ok) {
                const msg = await resp.text();
                throw new Error(msg || 'Failed to update time off request');
            }

            closeEditRequest();
            // Reload employee detail to show updated request
            if (selectedEmployee) {
                await loadEmployeeDetail(selectedEmployee.employee_id);
            }
            // Also reload the summary table
            load();
        } catch (err: any) {
            setEditError(err?.message || 'Failed to update time off request');
        } finally {
            setEditSubmitting(false);
        }
    };

    const handleDeleteRequest = async (requestId: string) => {
        if (!confirm('Are you sure you want to delete this time off request?')) {
            return;
        }

        try {
            const resp = await apiFetch(`/api/timesheet/time-off-requests/${requestId}`, {
                method: 'DELETE',
            });

            if (!resp.ok) {
                const msg = await resp.text();
                throw new Error(msg || 'Failed to delete time off request');
            }

            // Reload employee detail to show updated list
            if (selectedEmployee) {
                await loadEmployeeDetail(selectedEmployee.employee_id);
            }
            // Also reload the summary table
            load();
        } catch (err: any) {
            alert(err?.message || 'Failed to delete time off request');
        }
    };

    // Policy form state & handlers
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState<PolicyResponse | null>(null);
    const [form, setForm] = useState({ ...emptyForm });
    const [formError, setFormError] = useState<string | null>(null);
    // New Time Off Request modal state
    const browserTZ = useMemo(() => {
        try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
    }, []);
    const [showRequestModal, setShowRequestModal] = useState(false);
    const [requestSubmitting, setRequestSubmitting] = useState(false);
    const [requestError, setRequestError] = useState<string | null>(null);
    const [requestForm, setRequestForm] = useState({
        employee_id: '',
        policy_type_ref_id: '',
        date: new Date().toISOString().slice(0, 10),
        is_all_day: true,
        start_time: '09:00',
        end_time: '17:00',
        timezone: browserTZ,
        employee_note: ''
    });

    const openRequestModal = () => {
        setRequestError(null);
        setRequestForm({
            employee_id: '',
            policy_type_ref_id: '',
            date: new Date().toISOString().slice(0, 10),
            is_all_day: true,
            start_time: '09:00',
            end_time: '17:00',
            timezone: browserTZ,
            employee_note: ''
        });
        setShowRequestModal(true);
    };

    const toEpochSeconds = (dateStr: string, timeStr: string): number => {
        // Note: computed in the browser's local timezone; ensure timezone matches selected
        return Math.floor(new Date(`${dateStr}T${timeStr}:00`).getTime() / 1000);
    };

    const handleCreateTimeOff = async (e: React.FormEvent) => {
        e.preventDefault();
        setRequestError(null);
        setRequestSubmitting(true);

        try {
            if (!requestForm.employee_id) throw new Error('Employee is required');
            if (!requestForm.policy_type_ref_id) throw new Error('Policy type is required');
            if (!requestForm.date) throw new Error('Date is required');

            let start_ts = 0, end_ts = 0;
            if (requestForm.is_all_day) {
                start_ts = toEpochSeconds(requestForm.date, '00:00');
                end_ts = toEpochSeconds(requestForm.date, '23:59');
            } else {
                if (!requestForm.start_time || !requestForm.end_time) throw new Error('Start and end time are required');
                start_ts = toEpochSeconds(requestForm.date, requestForm.start_time);
                end_ts = toEpochSeconds(requestForm.date, requestForm.end_time);
                if (end_ts < start_ts) throw new Error('End time must be after start time');
            }

            const payload = {
                employee_id: requestForm.employee_id,
                policy_type_ref_id: requestForm.policy_type_ref_id,
                start_timestamp: start_ts,
                end_timestamp: end_ts,
                timezone: requestForm.timezone || browserTZ,
                is_all_day: requestForm.is_all_day,
                employee_note: requestForm.employee_note || null,
            };

            const resp = await apiFetch('/api/timesheet/time-off-requests', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!resp.ok) {
                const msg = await resp.text();
                throw new Error(msg || 'Failed to create time off request');
            }

            setShowRequestModal(false);
            // Refresh left table
            load();
        } catch (err: any) {
            setRequestError(err?.message || 'Failed to create time off request');
        } finally {
            setRequestSubmitting(false);
        }
    };


    const onCreateClick = () => {
        setEditing(null);
        setForm({ ...emptyForm });
        setShowForm(true);
        setFormError(null);
    };

    const onEditById = async (id: string) => {
        try {
            const r = await apiFetch(`/api/timesheet/time-off-policies/${id}`);
            if (!r.ok) return;
            const p: PolicyResponse = await r.json();
            setEditing(p);
            setForm({
                name: p.name,
                policy_type_id: p.policy_type_id,
                units: p.units,
                is_unlimited: p.is_unlimited,
                is_default: p.is_default ?? false,
                balance_limit: p.balance_limit ?? '',
                accrual_method: p.accrual_method,
                accrual_cycle: (p.accrual_cycle ?? 'all_at_once') as any,
                earned_units: p.earned_units ?? '',
                per_hours_worked: p.per_hours_worked ?? '',
                requires_admin_approval: p.requires_admin_approval,
                carryover_limit_enabled: p.carryover_limit_enabled,
                carryover_limit_value: p.carryover_limit_value ?? '',
                sun_hours: p.sun_hours ?? '',
                mon_hours: p.mon_hours ?? '',
                tue_hours: p.tue_hours ?? '',
                wed_hours: p.wed_hours ?? '',
                thu_hours: p.thu_hours ?? '',
                fri_hours: p.fri_hours ?? '',
                sat_hours: p.sat_hours ?? '',
                employee_ids: p.employee_ids || [],
            });
            setShowForm(true);
            setFormError(null);
        } catch { }
    };

    const closeForm = () => {
        setShowForm(false);
        setEditing(null);
        setForm({ ...emptyForm });
    };

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormError(null);

        const body: any = {
            name: form.name,
            policy_type_id: form.policy_type_id,
            units: form.units,
            is_unlimited: !!(form as any).is_unlimited,
            is_default: !!(form as any).is_default,
            balance_limit: (form as any).is_unlimited || form.balance_limit === '' ? null : Number(form.balance_limit),
            accrual_method: form.accrual_method,
            accrual_cycle: form.accrual_method === 'fixed_amount' ? form.accrual_cycle : null,
            earned_units: form.accrual_method === 'earned_per_hour' && form.earned_units !== '' ? Number(form.earned_units) : null,
            per_hours_worked: form.accrual_method === 'earned_per_hour' && form.per_hours_worked !== '' ? Number(form.per_hours_worked) : null,
            requires_admin_approval: !!form.requires_admin_approval,
            carryover_limit_enabled: !!form.carryover_limit_enabled,
            carryover_limit_value: form.carryover_limit_enabled && form.carryover_limit_value !== '' ? Number(form.carryover_limit_value) : null,
            sun_hours: form.sun_hours === '' ? null : Number(form.sun_hours),
            mon_hours: form.mon_hours === '' ? null : Number(form.mon_hours),
            tue_hours: form.tue_hours === '' ? null : Number(form.tue_hours),
            wed_hours: form.wed_hours === '' ? null : Number(form.wed_hours),
            thu_hours: form.thu_hours === '' ? null : Number(form.thu_hours),
            fri_hours: form.fri_hours === '' ? null : Number(form.fri_hours),
            sat_hours: form.sat_hours === '' ? null : Number(form.sat_hours),
            employee_ids: form.employee_ids,
        };

        const method = editing ? 'PUT' : 'POST';
        const path = editing ? `/api/timesheet/time-off-policies/${editing.id}` : '/api/timesheet/time-off-policies';

        const resp = await apiFetch(path, { method, body: JSON.stringify(body) });
        if (!resp.ok) {
            const msg = await resp.text();
            setFormError(msg || 'Request failed');
            return;
        }

        await resp.json();
        closeForm();
        reloadPolicies();
        load();
    };

    useEffect(() => { load(); }, [load]);

    const groupedPolicies = useMemo(() => {
        const map: Record<string, { typeName: string; items: PolicyItem[] }> = {};
        for (const p of policies) {
            const key = p.policy_type_id || 'unknown';
            if (!map[key]) map[key] = { typeName: p.policy_type_name || '—', items: [] };
            map[key].items.push(p);
        }
        return Object.values(map).sort((a, b) => a.typeName.localeCompare(b.typeName));
    }, [policies]);
    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
                            <SunIcon className="h-8 w-8" />
                            {t('timeoff.title', 'people')}
                        </h1>
                        <p className="text-muted-foreground">{t('timeoff.subtitle', 'people')}</p>
                    </div>
                    <div className="flex gap-2">
                        <PendingTimeOffRequestsButton />
                        <Button onClick={openRequestModal}>
                            <PlusIcon className="w-4 h-4 mr-2" />
                            {t('actions.addNew', 'common')}  {t('timeoff.singular', 'people')}
                        </Button>
                    </div>
                </div>

                <div >
                    <div className="flex items-center gap-4 mb-3">
                        <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
                            <SelectTrigger className="w-28">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {years.map((y) => (
                                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        <Select value={policyType} onValueChange={(v) => setPolicyType(v === '__all__' ? '' : v)}>
                            <SelectTrigger className="w-56">
                                <SelectValue placeholder={t('timeoff.allPolicyTypes', 'people')} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all__">{t('timeoff.allPolicyTypes', 'people')}</SelectItem>
                                {policyTypes.map((t) => (
                                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="border">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('timeoff.name', 'people')}</TableHead>
                                    <TableHead>{t('timeoff.approved', 'people')}</TableHead>
                                    <TableHead>{t('timeoff.remainingBalance', 'people')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.length ? rows.map((r) => (
                                    <TableRow
                                        key={r.employee_id}
                                        className="cursor-pointer hover:bg-gray-50"
                                        onClick={() => loadEmployeeDetail(r.employee_id)}
                                    >
                                        <TableCell className="font-medium">{r.first_name} {r.last_name}</TableCell>
                                        <TableCell>{(r.approved_hours || 0).toFixed(2)} {t('units.hours', 'common')}</TableCell>
                                        <TableCell>
                                            {r.is_unlimited ? t('timeoff.unlimited', 'people') : (r.remaining_hours === null ? '—' : `${r.remaining_hours.toFixed(2)} ${t('units.hours', 'common')}`)}
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                                            {loading ? t('common.loading', 'common') : t('timeoff.noData', 'people')}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </div>

                {/* Create Time Off Request Modal */}
                <Dialog open={showRequestModal} onOpenChange={(open) => setShowRequestModal(open)}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle>{t('actions.addNew', 'common')}  {t('timeoff.singular', 'people')}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleCreateTimeOff} className="space-y-4">
                            {requestError && (
                                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">{requestError}</div>
                            )}

                            <div>
                                <Label>{t('common.employee', 'people')} *</Label>
                                <Select value={requestForm.employee_id} onValueChange={(value) => setRequestForm({ ...requestForm, employee_id: value })}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={t('contracts.selectEmployee', 'people')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {employees.map((emp) => (
                                            <SelectItem key={emp.id} value={emp.id}>
                                                {emp.first_name} {emp.last_name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <Label>{t('timeoff.policyType', 'people')} *</Label>
                                <Select value={requestForm.policy_type_ref_id} onValueChange={(value) => setRequestForm({ ...requestForm, policy_type_ref_id: value })}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={t('timeoff.selectPolicyType', 'people')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {policyTypes.map((t) => (
                                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <Label>{t('forms.date', 'common')} *</Label>
                                    <Input type="date" value={requestForm.date} onChange={(e) => setRequestForm({ ...requestForm, date: e.target.value })} required />
                                </div>
                                <div className="flex items-end">
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={requestForm.is_all_day}
                                            onChange={(e) => setRequestForm({ ...requestForm, is_all_day: e.target.checked })}
                                        />
                                        <span>{t('timeoff.fullDay', 'people')}</span>
                                    </label>
                                </div>
                            </div>

                            {!requestForm.is_all_day && (
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label>{t('timeoff.startTime', 'people')} *</Label>
                                        <Input type="time" value={requestForm.start_time} onChange={(e) => setRequestForm({ ...requestForm, start_time: e.target.value })} required />
                                    </div>
                                    <div>
                                        <Label>{t('timeoff.endTime', 'people')} *</Label>
                                        <Input type="time" value={requestForm.end_time} onChange={(e) => setRequestForm({ ...requestForm, end_time: e.target.value })} required />
                                    </div>
                                </div>
                            )}

                            <div>
                                <Label>{t('timeoff.timezone', 'people')}</Label>
                                <Input value={requestForm.timezone} onChange={(e) => setRequestForm({ ...requestForm, timezone: e.target.value })} />
                            </div>

                            <div>
                                <Label>{t('timeoff.note', 'people')}</Label>
                                <textarea
                                    className="w-full border rounded px-2 py-1"
                                    rows={3}
                                    value={requestForm.employee_note}
                                    onChange={(e) => setRequestForm({ ...requestForm, employee_note: e.target.value })}
                                />
                            </div>

                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setShowRequestModal(false)}>{t('actions.cancel', 'common')}</Button>
                                <Button type="submit" disabled={requestSubmitting}>{requestSubmitting ? t('timeoff.creating', 'people') : t('actions.create', 'common')}</Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>

                {/* Employee Time Off Detail Popup */}
                <Dialog open={!!selectedEmployee} onOpenChange={(open) => !open && setSelectedEmployee(null)}>
                    <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
                        {selectedEmployee && (
                            <>
                                <DialogHeader>
                                    <DialogTitle className="flex items-center gap-2">
                                        <span>{selectedEmployee.first_name} {selectedEmployee.last_name} - {t('timeoff.timeOffPolicies', 'people')}</span>
                                    </DialogTitle>
                                </DialogHeader>

                                <div className="space-y-6">
                                    {/* Time Off Balances */}
                                    <div>
                                        <h3 className="font-semibold mb-3">{t('timeoff.timeOffBalances', 'people')}</h3>
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                            {selectedEmployee.policy_balances.map((balance) => {
                                                // Determine which balance to display based on units
                                                const displayValue = balance.is_unlimited
                                                    ? '∞'
                                                    : balance.units === 'days' && balance.balance_days !== null
                                                        ? balance.balance_days.toFixed(1)
                                                        : balance.balance_hours !== null
                                                            ? balance.balance_hours.toFixed(1)
                                                            : '—';

                                                const displayUnit = balance.is_unlimited
                                                    ? t('timeoff.unlimitedPolicy', 'people')
                                                    : balance.units === 'days'
                                                        ? t('timeoff.daysRemaining', 'people')
                                                        : t('timeoff.hoursRemaining', 'people');

                                                return (
                                                    <div key={balance.policy_type_id} className="border rounded-lg p-4 text-center">
                                                        <div className="text-sm text-gray-600 mb-1">{balance.policy_type_name}</div>
                                                        <div className="text-3xl font-bold">
                                                            {displayValue}
                                                        </div>
                                                        <div className="text-xs text-gray-500 mt-1">
                                                            {displayUnit}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            {selectedEmployee.policy_balances.length === 0 && (
                                                <div className="col-span-3 text-center text-gray-500 py-4">
                                                    {t('timeoff.noPoliciesAssigned', 'people')}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Time Off Requests */}
                                    <div>
                                        <div className="flex items-center justify-between mb-3">
                                            <h3 className="font-semibold">{t('timeoff.timeOffRequests', 'people')}</h3>
                                            <div className="text-sm text-gray-600">
                                                {t('timeoff.totalApproved', 'people')}: <span className="font-semibold">{selectedEmployee.total_approved_hours.toFixed(1)} {t('units.hours', 'common')}</span>
                                            </div>
                                        </div>

                                        <div className="space-y-3">
                                            {selectedEmployee.requests.map((req) => {
                                                const startDate = new Date(req.start_timestamp * 1000);
                                                const endDate = new Date(req.end_timestamp * 1000);
                                                const formatDate = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                                                const formatTime = (d: Date) => d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

                                                const statusColors = {
                                                    approved: 'bg-green-50 border-green-200 text-green-700',
                                                    pending: 'bg-yellow-50 border-yellow-200 text-yellow-700',
                                                    denied: 'bg-red-50 border-red-200 text-red-700',
                                                };
                                                const statusColor = statusColors[req.status as keyof typeof statusColors] || 'bg-gray-50 border-gray-200 text-gray-700';

                                                return (
                                                    <div key={req.id} className={`border rounded-lg p-3 ${statusColor}`}>
                                                        <div className="flex items-start justify-between mb-2">
                                                            <div className="flex-1">
                                                                <div className="font-medium text-sm">{req.policy_type_name}</div>
                                                                <div className="text-xs mt-1">
                                                                    {formatDate(startDate)}
                                                                    {req.is_all_day ? (
                                                                        <span className="ml-1">({t('timeoff.fullDay', 'people')})</span>
                                                                    ) : (
                                                                        <span className="ml-1">
                                                                            {formatTime(startDate)} → {formatTime(endDate)}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-xs font-semibold uppercase px-2 py-1 rounded">
                                                                    {req.status}
                                                                </span>
                                                                {req.status === 'pending' && (
                                                                    <>
                                                                        <button
                                                                            onClick={() => openEditRequest(req)}
                                                                            className="text-xs text-blue-600 hover:text-blue-800 underline"
                                                                        >
                                                                            {t('actions.edit', 'common')}
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleDeleteRequest(req.id)}
                                                                            className="text-xs text-red-600 hover:text-red-800 underline"
                                                                        >
                                                                            {t('actions.delete', 'common')}
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {req.employee_note && (
                                                            <div className="text-xs mt-2 pt-2 border-t border-current/20">
                                                                <span className="font-medium">{t('timeoff.note', 'people')}:</span> {req.employee_note}
                                                            </div>
                                                        )}

                                                        {req.manager_note && (
                                                            <div className="text-xs mt-1">
                                                                <span className="font-medium">{t('timeoff.adminNote', 'people')}:</span> {req.manager_note}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                            {selectedEmployee.requests.length === 0 && (
                                                <div className="text-center text-gray-500 py-8 border rounded-lg">
                                                    {t('timeoff.noRequestsForYear', 'people')} {selectedEmployee.year}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <DialogFooter>
                                    <Button variant="outline" onClick={() => setSelectedEmployee(null)}>{t('actions.close', 'common')}</Button>
                                </DialogFooter>
                            </>
                        )}
                        {detailLoading && (
                            <div className="flex items-center justify-center py-12">
                                <div className="text-gray-500">{t('common.loading', 'common')}</div>
                            </div>
                        )}
                    </DialogContent>
                </Dialog>

                {/* Edit Time Off Request Dialog */}
                <Dialog open={!!editingRequest} onOpenChange={(open) => !open && closeEditRequest()}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle>{t('timeoff.editRequest', 'people')}</DialogTitle>
                        </DialogHeader>
                        {editingRequest && (
                            <form onSubmit={handleUpdateRequest} className="space-y-4">
                                {editError && (
                                    <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">{editError}</div>
                                )}

                                <div>
                                    <Label>{t('timeoff.policyType', 'people')}</Label>
                                    <Input value={editingRequest.policy_type_name} disabled className="bg-gray-50" />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                        <Label>{t('forms.date', 'common')} *</Label>
                                        <Input
                                            type="date"
                                            name="date"
                                            defaultValue={new Date(editingRequest.start_timestamp * 1000).toISOString().slice(0, 10)}
                                            required
                                        />
                                    </div>
                                    <div className="flex items-end">
                                        <label className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                name="is_all_day"
                                                defaultChecked={editingRequest.is_all_day}
                                            />
                                            <span>{t('timeoff.fullDay', 'people')}</span>
                                        </label>
                                    </div>
                                </div>

                                {!editingRequest.is_all_day && (
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label>{t('timeoff.startTime', 'people')} *</Label>
                                            <Input
                                                type="time"
                                                name="start_time"
                                                defaultValue={new Date(editingRequest.start_timestamp * 1000).toTimeString().slice(0, 5)}
                                                required
                                            />
                                        </div>
                                        <div>
                                            <Label>{t('timeoff.endTime', 'people')} *</Label>
                                            <Input
                                                type="time"
                                                name="end_time"
                                                defaultValue={new Date(editingRequest.end_timestamp * 1000).toTimeString().slice(0, 5)}
                                                required
                                            />
                                        </div>
                                    </div>
                                )}

                                <div>
                                    <Label>{t('timeoff.timezone', 'people')}</Label>
                                    <Input name="timezone" defaultValue={browserTZ} />
                                </div>

                                <div>
                                    <Label>{t('timeoff.note', 'people')}</Label>
                                    <textarea
                                        className="w-full border rounded px-2 py-1"
                                        rows={3}
                                        name="employee_note"
                                        defaultValue={editingRequest.employee_note || ''}
                                    />
                                </div>

                                <DialogFooter>
                                    <Button type="button" variant="outline" onClick={closeEditRequest}>{t('actions.cancel', 'common')}</Button>
                                    <Button type="submit" disabled={editSubmitting}>{editSubmitting ? t('timeoff.saving', 'people') : t('actions.save', 'common')}</Button>
                                </DialogFooter>
                            </form>
                        )}
                    </DialogContent>
                </Dialog>

            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/people/timeoff\/')({
    component: TimeOffOverviewPage,
})
